/**
 * Auto-Translation Hook for Gauntlet Debates
 * Automatically translates missing i18n keys using DeepL
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { translateText } from '../services/translationService';
import logger from '../utils/logger';

interface UseAutoTranslationOptions {
  fallbackToOriginal?: boolean;
  enableLogging?: boolean;
}

/**
 * Hook that automatically translates text when i18n key is missing
 * Falls back to DeepL translation if the key doesn't exist in current language
 */
export const useAutoTranslation = (options: UseAutoTranslationOptions = {}) => {
  const { i18n, t } = useTranslation();
  const { enableLogging = false } = options;

  /**
   * Get translated text - uses i18n first, then auto-translates if missing
   */
  const at = (key: string, defaultText?: string): string => {
    // Try to get translation from i18n first
    const translatedText = t(key, { defaultValue: null });
    
    // If translation exists and is not the key itself, return it
    if (translatedText && translatedText !== key) {
      return translatedText;
    }

    // Use defaultText if provided, otherwise use the key
    const sourceText = defaultText || key;
    
    if (enableLogging) {
      logger.info(`🔍 Missing translation for "${key}" in ${i18n.language}, using source: "${sourceText}"`);
    }

    // For English, just return the source text
    if (i18n.language === 'en') {
      return sourceText;
    }

    // Return source text for now - async translation will be handled by useAsyncTranslation
    return sourceText;
  };

  return { at };
};

/**
 * Hook for async auto-translation with loading states
 * Use this when you can handle loading states in your UI
 */
export const useAsyncTranslation = (
  text: string, 
  options: UseAutoTranslationOptions = {}
) => {
  const { i18n } = useTranslation();
  const [translation, setTranslation] = useState<string>(text);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { enableLogging = false } = options;

  useEffect(() => {
    // Skip translation for English or empty text
    if (i18n.language === 'en' || !text) {
      setTranslation(text);
      return;
    }

    const translateAsync = async () => {
      setIsLoading(true);
      setError(null);

      try {
        if (enableLogging) {
          logger.info(`🌍 Auto-translating: "${text}" to ${i18n.language}`);
        }

        const result = await translateText(text, i18n.language);
        setTranslation(result);

        if (enableLogging) {
          logger.info(`✅ Auto-translation result: "${result}"`);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Translation failed';
        setError(errorMessage);
        setTranslation(text); // Fallback to original text
        
        if (enableLogging) {
          logger.error(`❌ Auto-translation failed: ${errorMessage}`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    translateAsync();
  }, [text, i18n.language, enableLogging]);

  return {
    translation,
    isLoading,
    error,
    isTranslated: translation !== text && i18n.language !== 'en'
  };
};

/**
 * Batch translation hook for multiple texts
 * Efficient for translating multiple items at once
 */
export const useBatchTranslation = (texts: string[]) => {
  const { i18n } = useTranslation();
  const [translations, setTranslations] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (i18n.language === 'en' || texts.length === 0) {
      // For English, just map texts to themselves
      const englishMap = texts.reduce((acc, text) => {
        acc[text] = text;
        return acc;
      }, {} as Record<string, string>);
      setTranslations(englishMap);
      return;
    }

    const translateBatch = async () => {
      setIsLoading(true);
      try {
        const { translateBatch } = await import('../services/translationService');
        const results = await translateBatch(texts, i18n.language);
        setTranslations(results);
      } catch (error) {
        logger.error('Batch translation failed:', error);
        // Fallback to original texts
        const fallbackMap = texts.reduce((acc, text) => {
          acc[text] = text;
          return acc;
        }, {} as Record<string, string>);
        setTranslations(fallbackMap);
      } finally {
        setIsLoading(false);
      }
    };

    translateBatch();
  }, [texts, i18n.language]);

  return {
    translations,
    isLoading,
    getTranslation: (text: string) => translations[text] || text
  };
}; 